import { useId, useMemo } from 'react';
import {
  Box,
  Heading,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Sparkles, Users, DollarSign } from 'lucide-react';
import { Restaurant } from '@/types/restaurant';
import {
  getCompatibilityEmoji,
  hasPersonalRating,
  getCompatibilityColor,
  getCompatibilityLevel,
  COMPATIBILITY_LEVELS,
  RATING_EMOJIS,
  visuallyHiddenSx,
} from '@/utils/restaurantUtils';
import { compatibilityModalCopy, type CompatibilityLevel } from '@/copy/compatibilityModal';

interface CompatibilityModalProps {
  restaurant: Restaurant;
  friendCount?: number;
  isOpen: boolean;
  onClose: () => void;
}

interface MatchReason {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
}

/** Key rows listed best (Highest, level 5) to worst (No, level 0). */
const KEY_LEVELS: CompatibilityLevel[] = [5, 4, 3, 2, 1, 0];

export function CompatibilityModal({
  restaurant,
  friendCount = 0,
  isOpen,
  onClose,
}: CompatibilityModalProps) {
  // Same predicate the emoji/color/level helpers use (`hasPersonalRating`):
  // a rating only counts as personal TasteMatch™ data when `scoreSource` is
  // absent, `self_rating`, or `taste_match`. A `public_consensus` / `embedding`
  // score can carry a numeric `rating` yet is not personal, so a bare
  // `rating != null` check would disagree with the helpers and render a tier
  // key for a non-personal score. Gating here keeps the "Still Learning"
  // state and the helpers in lockstep, and drives `hasPublicRatingOnly` below.
  const hasPersonalData = hasPersonalRating(restaurant);
  const keyHeadingId = useId();
  // Product decision (paire-app#1 R2): TasteMatch™ is reserved for real
  // member<->restaurant compatibility. When there's no personal data but a
  // public rating exists, this modal must keep its honest "Still Learning"
  // copy (never fabricate a TasteMatch from the public rating) AND explicitly
  // clarify that a public rating exists so it doesn't read as a flat "we know
  // nothing about this restaurant" — that clarification is the fix for the
  // contradiction where RestaurantHero's badge used to say "TasteMatch" for
  // this exact case.
  const hasPublicRatingOnly = !hasPersonalData && restaurant.googleRating != null;

  const emoji = hasPersonalData ? getCompatibilityEmoji(restaurant) : null;
  const bgColor = hasPersonalData ? getCompatibilityColor(restaurant) : '#a6a6a5';
  const level = hasPersonalData ? getCompatibilityLevel(restaurant) : 0;
  const currentLevel = Math.max(0, Math.min(5, level)) as CompatibilityLevel;

  // Derive match reasons from available data
  const matchReasons = useMemo(() => {
    if (!hasPersonalData) return [];

    const reasons: MatchReason[] = [];

    // Friend activity reason
    if (friendCount > 0) {
      reasons.push({
        icon: <Users size={20} />,
        title: `${friendCount} friend${friendCount > 1 ? 's' : ''} visited`,
        description: 'People in your network have been here',
        color: 'purple.500',
      });
    }

    // Price level match (if available)
    const priceLevel = restaurant.priceLevel;
    if (priceLevel && priceLevel >= 1 && priceLevel <= 4) {
      const priceLabels = ['Budget-friendly', 'Moderate', 'Upscale', 'Fine Dining'];
      reasons.push({
        icon: <DollarSign size={20} />,
        title: priceLabels[priceLevel - 1],
        description: 'Price range fits your dining style',
        color: 'teal.500',
      });
    }

    return reasons;
  }, [hasPersonalData, friendCount, restaurant.priceLevel]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent
        bg="var(--card-bg)"
        maxW="400px"
        maxH="calc(100dvh - 32px)"
        mx={4}
        borderRadius="var(--r-card)"
        overflow="hidden"
      >
        {/* Direct child of ModalContent (not ModalHeader) so the heading's
            accessible name stays just the tier name instead of absorbing the
            close button's label. Absolutely positioned over the header; with
            scrollBehavior="inside" the header stays pinned, so it does too. */}
        <ModalCloseButton
          position="absolute"
          top={3}
          right={3}
          zIndex={1}
          color="blackAlpha.600"
          _hover={{ color: 'blackAlpha.800' }}
        />
        <ModalHeader
          bg={bgColor}
          py={6}
          px={4}
          flexShrink={0}
        >
          <VStack spacing={3} color="blackAlpha.800">
            {/* The emoji / no-data icon sits directly on the tier-colored header
                (no backing circle). The Sparkles fallback has no color of its own:
                lucide strokes with currentColor, so it inherits the dark ink set
                once on the enclosing VStack (shared with the heading) and keeps
                contrast on the grey fallback in both modes. */}
            {hasPersonalData && emoji ? (
              // alt="": the heading below already names the tier.
              <Image src={emoji.icon} alt="" boxSize="48px" />
            ) : (
              <Box boxSize="48px" display="flex" alignItems="center" justifyContent="center">
                <Sparkles size={32} />
              </Box>
            )}
            <VStack spacing={1}>
              {/* Card-token sweep: bgColor is a fixed compatibility-level
                  pastel (getCompatibilityColor()), not --card-bg — it never
                  changes with theme. --card-text/-2 flip to near-white in
                  dark mode and go near-invisible on this always-light
                  pastel, so this header uses the same mode-invariant
                  blackAlpha family as the ModalCloseButton right above it. The
                  ink is set once on the parent VStack and inherited here. */}
              <Heading size="lg" textAlign="center">
                {hasPersonalData && emoji ? emoji.label : compatibilityModalCopy.stillLearning}
              </Heading>
              {/* blackAlpha.800, not .700: the "No TasteMatch" red pastel is the
                  darkest tier background and .700 lands at 3.97:1 there —
                  .800 clears 4.5:1 on every tier.
                  D66 disposition: this Heading/Text pair appears in the
                  contrast sweep's baseline `violations` list, but is a
                  documented false positive, not a defect — axe scanned
                  before Chakra Modal's entrance transition settled and
                  captured a partially-composited color (implied alpha
                  ≈0.3), not blackAlpha.800's true rgba(0,0,0,0.80). At the
                  real, settled alpha this clears 4.5:1 on every
                  COMPATIBILITY_LEVELS color and the "not sure" grey
                  fallback (worst case, red #f43636, is 4.59:1) — confirmed
                  independently by ui-ux-designer. No code change. */}
              <Text fontSize="sm" color="blackAlpha.800" textAlign="center">
                {compatibilityModalCopy.headerSubtitle}
              </Text>
            </VStack>
          </VStack>
        </ModalHeader>

        {/* D66: key labels, reason descriptions, and explanatory copy use the token-driven --card-text/-2 pair;
            verified passing at rest. See docs/current_work/sdlc-lite/
            d66_incomplete_dispositions.md for the investigation history. */}
        <ModalBody py={5} px={4} overflowY="auto">
          {hasPersonalData ? (
            <>
              {/* Legacy "Compatibility Key": all six tiers, best first. The
                  member's current tier is ringed and bolded, and announced to
                  assistive tech with aria-current plus a hidden marker. */}
              <Heading as="h3" id={keyHeadingId} size="xs" color="var(--card-text)" mb={2}>
                {compatibilityModalCopy.keyHeading}
              </Heading>
              {/* role="list": VoiceOver/WebKit drops list semantics from a ul with
                  list-style none unless the role is explicit. */}
              <VStack as="ul" role="list" aria-labelledby={keyHeadingId} listStyleType="none" spacing={1} align="stretch" mb={4}>
                {KEY_LEVELS.map((keyLevel) => {
                  const isCurrent = keyLevel === currentLevel;
                  return (
                    <HStack
                      as="li"
                      key={keyLevel}
                      data-current={isCurrent ? 'true' : undefined}
                      aria-current={isCurrent ? 'true' : undefined}
                      spacing={3}
                      px={2}
                      py={0.5}
                      bg={isCurrent ? 'var(--card-surface-2)' : 'transparent'}
                      boxShadow={isCurrent ? '0 0 0 2px var(--card-text)' : 'none'}
                      // Forced-colors mode drops box-shadow; a transparent outline
                      // is repainted in a system color so the ring survives.
                      outline={isCurrent ? '2px solid transparent' : undefined}
                      borderRadius="var(--r-chip)"
                    >
                      <Box
                        bg={COMPATIBILITY_LEVELS[keyLevel].primaryColor}
                        borderRadius="full"
                        boxSize="28px"
                        border="1px solid"
                        borderColor="var(--card-dashed)"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        <Image src={RATING_EMOJIS[keyLevel].icon} alt="" boxSize="20px" />
                      </Box>
                      <Text
                        fontSize="sm"
                        fontWeight={isCurrent ? 'bold' : 'normal'}
                        color="var(--card-text)"
                      >
                        {compatibilityModalCopy.keyRowLabel(RATING_EMOJIS[keyLevel].label)}
                        {isCurrent && (
                          <Box as="span" sx={visuallyHiddenSx}>
                            {' '}
                            {compatibilityModalCopy.currentTierMarker}
                          </Box>
                        )}
                      </Text>
                    </HStack>
                  );
                })}
              </VStack>
              <VStack spacing={3} align="stretch">
                {matchReasons.map((reason, index) => (
                  <HStack
                    key={index}
                    p={3}
                    bg="var(--card-surface-2)"
                    borderRadius="var(--r-chip)"
                    spacing={3}
                  >
                    <Box
                      color={reason.color}
                      bg="var(--card-bg)"
                      p={2}
                      borderRadius="var(--r-inner)"
                      boxShadow="sm"
                      flexShrink={0}
                    >
                      {reason.icon}
                    </Box>
                    <VStack align="start" spacing={0} flex={1}>
                      <Text fontSize="sm" fontWeight="semibold" color="var(--card-text)">
                        {reason.title}
                      </Text>
                      {/* D66: --card-text-3 fails the 4.5:1 normal-text
                          floor here (≈3.17:1 light/≈3.42:1 dark vs
                          --card-surface-2) — --card-text-2 clears
                          ≈4.89:1/≈5.79:1. */}
                      <Text fontSize="xs" color="var(--card-text-2)">
                        {reason.description}
                      </Text>
                    </VStack>
                  </HStack>
                ))}
              </VStack>
              {/* D66: --card-text-3 fails the 4.5:1 normal-text floor here
                  (≈3.45:1 light/≈3.87:1 dark vs --card-bg) — --card-text-2
                  clears ≈5.33:1/≈6.55:1. */}
              <VStack spacing={2} align="stretch" mt={5}>
                {compatibilityModalCopy.keyExplanation.map((paragraph) => (
                  <Text key={paragraph} fontSize="xs" color="var(--card-text-2)">
                    {paragraph}
                  </Text>
                ))}
              </VStack>
            </>
          ) : (
            <HStack p={3} bg="var(--card-surface-2)" borderRadius="var(--r-chip)" spacing={3}>
              <Box
                color="var(--card-text-3)"
                bg="var(--card-bg)"
                p={2}
                borderRadius="var(--r-inner)"
                boxShadow="sm"
                flexShrink={0}
              >
                <Sparkles size={20} />
              </Box>
              <VStack align="start" spacing={0} flex={1}>
                <Text fontSize="sm" fontWeight="semibold" color="var(--card-text)">
                  {compatibilityModalCopy.noDataTitle}
                </Text>
                <Text fontSize="xs" color="var(--card-text-2)">
                  {compatibilityModalCopy.noDataBody}
                </Text>
                {hasPublicRatingOnly && (
                  <Text fontSize="xs" color="var(--card-text-2)" mt={2}>
                    {compatibilityModalCopy.publicRatingParagraph(restaurant.googleRating!)}
                  </Text>
                )}
              </VStack>
            </HStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default CompatibilityModal;
